import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import { displayQueue, computeQueuePrefix, extractExtension } from './utils';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const AGENTS_POLL_INTERVAL_MS = 20000;

const SCHEDULE_STATE_LABELS = {
  on: { label: 'Ativo', color: '#22c55e', bg: '#f0fdf4' },
  off: { label: 'Inativo', color: '#6b7280', bg: '#f9fafb' },
  forced_on: { label: 'Forçado On', color: '#3b82f6', bg: '#eff6ff' },
  forced_off: { label: 'Forçado Off', color: '#f59e0b', bg: '#fffbeb' },
};

function ScheduleBadge({ state }) {
  const cfg = SCHEDULE_STATE_LABELS[state] || SCHEDULE_STATE_LABELS.off;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.color}40`,
    }}>
      {cfg.label}
    </span>
  );
}

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

function AgentsConfig() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [queues, setQueues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    extension: '',
    email: '',
    selectedSkills: [],
    selectedQueues: [],
  });
  const [scheduleStates, setScheduleStates] = useState({});
  const [forceLoading, setForceLoading] = useState({});
  const [queuePrefix, setQueuePrefix] = useState(null);
  const [tenantIdState, setTenantIdState] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const hasLoadedOnceRef = useRef(false);

  const effectivePrefix = useMemo(
    () => queuePrefix || computeQueuePrefix(String(tenantIdState).toLowerCase(), queues.map((q) => q.name)),
    [queuePrefix, tenantIdState, queues],
  );

  const fetchData = useCallback(async () => {
    try {
      if (!hasLoadedOnceRef.current) setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();
      setTenantIdState(tenantId);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': tenantId,
      };

      const [agentsRes, skillsRes, queuesRes] = await Promise.all([
        fetch(`${apiBase}/v2/callcenter/agents`, { headers }),
        fetch(`${apiBase}/v2/callcenter/skills`, { headers }),
        fetch(`${apiBase}/v2/callcenter/queues`, { headers }),
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        const agentsData = data.agents || data || [];
        setAgents(agentsData);
        fetchScheduleStates(agentsData, apiBase, tenantId, token);
      }
      if (skillsRes.ok) {
        const data = await skillsRes.json();
        setSkills(data.skills || data || []);
      }
      if (queuesRes.ok) {
        const data = await queuesRes.json();
        setQueues(data.queues || data || []);
        setQueuePrefix(data.namePrefix ?? null);
      }
    } catch (e) {
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, AGENTS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const isPersisted = !!(editingAgent && editingAgent.idCallcenterAgent);
      const isLiveSyncedEdit = !!editingAgent && !isPersisted;

      const url = isPersisted
        ? `${apiBase}/v2/callcenter/agents/${encodeURIComponent(editingAgent.agentKey)}`
        : `${apiBase}/v2/callcenter/agents`;

      const method = isPersisted ? 'PUT' : 'POST';

      const body = {
        displayName: formData.name,
        extension: formData.extension,
        email: formData.email,
        skills: formData.selectedSkills.map((sk) => ({
          skillKey: sk,
          level: 100,
        })),
        queues: formData.selectedQueues,
      };

      if (!isPersisted) {
        body.agentKey = formData.extension;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId,
      };

      let response = await fetch(url, { method, headers, body: JSON.stringify(body) });

      if (!response.ok && response.status === 409 && isLiveSyncedEdit) {
        const retryUrl = `${apiBase}/v2/callcenter/agents/${encodeURIComponent(formData.extension)}`;
        response = await fetch(retryUrl, { method: 'PUT', headers, body: JSON.stringify(body) });
      }

      if (response.ok) {
        setShowModal(false);
        setEditingAgent(null);
        resetForm();
        fetchData();
        setSuccess(isPersisted ? 'Agente atualizado com sucesso.' : 'Agente criado com sucesso.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(`Erro ao salvar agente: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      setError(`Erro ao salvar agente. Verifique sua conexão.${e?.message ? ` (${e.message})` : ''}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/agents/${encodeURIComponent(deleteConfirm.agentKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        setSuccess(`Agente ${deleteConfirm.displayName || deleteConfirm.extension || deleteConfirm.agentKey} excluído.`);
        setTimeout(() => setSuccess(null), 3000);
        fetchData();
        closeDeleteConfirm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setDeleteError(`Erro ao excluir agente: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      setDeleteError(`Erro ao excluir agente. Verifique sua conexão.${e?.message ? ` (${e.message})` : ''}`);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = (agent) => {
    setDeleteError(null);
    setDeleteConfirm(agent);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm(null);
    setDeleteError(null);
  };

  const openEditModal = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.displayName || agent.name || '',
      extension: agent.extension || extractExtension(agent.agentKey) || '',
      email: agent.email || '',
      selectedSkills: agent.skills?.map((s) => s.skillKey) || [],
      selectedQueues: agent.queueMemberships?.length
        ? agent.queueMemberships.map((m) => ({ queueKey: m.queueKey, penalty: m.penalty ?? 0 }))
        : (agent.queues || []).map((q) => ({ queueKey: q, penalty: 0 })),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      extension: '',
      email: '',
      selectedSkills: [],
      selectedQueues: [],
    });
  };

  const openNewModal = () => {
    setEditingAgent(null);
    resetForm();
    setShowModal(true);
  };

  const toggleSkill = (skillKey) => {
    setFormData((prev) => ({
      ...prev,
      selectedSkills: prev.selectedSkills.includes(skillKey)
        ? prev.selectedSkills.filter((s) => s !== skillKey)
        : [...prev.selectedSkills, skillKey],
    }));
  };

  const toggleQueue = (queueKey) => {
    setFormData((prev) => {
      const exists = prev.selectedQueues.some((q) => q.queueKey === queueKey);
      return {
        ...prev,
        selectedQueues: exists
          ? prev.selectedQueues.filter((q) => q.queueKey !== queueKey)
          : [...prev.selectedQueues, { queueKey, penalty: 0 }],
      };
    });
  };

  const setQueuePenalty = (queueKey, penalty) => {
    setFormData((prev) => ({
      ...prev,
      selectedQueues: prev.selectedQueues.map((q) =>
        q.queueKey === queueKey ? { ...q, penalty } : q
      ),
    }));
  };

  const fetchScheduleStates = useCallback(async (agentsList, apiBase, tenantId, token) => {
    const headers = { 'Authorization': `Bearer ${token}`, 'x-tenant-id': tenantId };
    try {
      const res = await fetch(`${apiBase}/v2/callcenter/agents/schedule-states`, { headers });
      if (res.ok) {
        const data = await res.json();
        setScheduleStates(data && typeof data === 'object' ? data : {});
      }
    } catch { /* noop */ }
  }, []);

  const handleForceSchedule = useCallback(async (agentKey, action) => {
    setForceLoading(prev => ({ ...prev, [agentKey]: action }));
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();
      const res = await fetch(`${apiBase}/v2/callcenter/agents/${encodeURIComponent(agentKey)}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-tenant-id': tenantId, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttlMinutes: 240 }),
      });
      if (res.ok) {
        const expectedState = action === 'force-enable' ? 'forced_on'
          : action === 'force-disable' ? 'forced_off'
          : null;
        setScheduleStates(prev => ({ ...prev, [agentKey]: expectedState }));
      }
    } catch { /* noop */ }
    finally {
      setForceLoading(prev => ({ ...prev, [agentKey]: null }));
    }
  }, []);

  const isRegisteredOutOfQueue = (agent) => agent.status === 'offline' && agent.registryState === 'online';

  const getStatusColor = (agent) => {
    if (isRegisteredOutOfQueue(agent)) return '#3b82f6';
    const colors = {
      available: '#22c55e',
      busy: '#ef4444',
      paused: '#8b5cf6',
      offline: '#6b7280',
      wrapup: '#f59e0b',
    };
    return colors[agent.status] || colors.offline;
  };

  const getStatusLabel = (agent) => {
    if (isRegisteredOutOfQueue(agent)) return 'Registrado, fora de fila';
    const labels = {
      available: 'Disponível',
      busy: 'Ocupado',
      paused: 'Pausado',
      offline: 'Offline',
      wrapup: 'Pós-atendimento',
    };
    return labels[agent.status] || 'Offline';
  };

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Gestão de Agentes</h1>
          <p>Gerencie os agentes do Call Center e suas habilidades</p>
        </div>
        <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
          + Novo Agente
        </button>
      </div>

      {success && (
        <div className="ccv2-p5-success">
          {success}
          <button onClick={() => setSuccess(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}
      {error && (
        <div className="ccv2-p5-error">
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando agentes...</p>
        </div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Nome</th>
                  <th>Ramal</th>
                  <th>Skills</th>
                  <th>Filas</th>
                  <th>Escala</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="ccv2-empty-row">
                      Nenhum agente cadastrado
                    </td>
                  </tr>
                ) : (
                  agents.map((agent) => (
                    <tr key={agent.agentKey || agent.id_callcenter_agent}>
                      <td>
                        <span
                          className="ccv2-status-dot"
                          style={{ backgroundColor: getStatusColor(agent) }}
                          title={getStatusLabel(agent)}
                        />
                      </td>
                      <td className="ccv2-cell-name">{agent.displayName || agent.name}</td>
                      <td>{agent.extension || agent.agentKey}</td>
                      <td>
                        <div className="ccv2-tags">
                          {agent.skills?.slice(0, 3).map((skill) => (
                            <span key={skill.skillKey} className="ccv2-tag">
                              {skill.skillKey}
                            </span>
                          ))}
                          {agent.skills?.length > 3 && (
                            <span className="ccv2-tag ccv2-tag-more">
                              +{agent.skills.length - 3}
                            </span>
                          )}
                          {(!agent.skills || agent.skills.length === 0) && (
                            <span className="ccv2-tag ccv2-tag-empty">Nenhuma</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="ccv2-tags">
                          {agent.queues?.slice(0, 2).map((queue) => (
                            <span key={queue} className="ccv2-tag ccv2-tag-queue" title={queue}>
                              {displayQueue(queue, effectivePrefix)}
                            </span>
                          ))}
                          {agent.queues?.length > 2 && (
                            <span className="ccv2-tag ccv2-tag-more">
                              +{agent.queues.length - 2}
                            </span>
                          )}
                          {(!agent.queues || agent.queues.length === 0) && (
                            <span className="ccv2-tag ccv2-tag-empty">Nenhuma</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {scheduleStates[agent.agentKey] != null ? (
                          <ScheduleBadge state={scheduleStates[agent.agentKey]} />
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td className="ccv2-cell-actions">
                        <button
                          className="ccv2-btn-icon ccv2-btn-edit"
                          onClick={() => openEditModal(agent)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="ccv2-btn-icon ccv2-btn-delete"
                          onClick={() => openDeleteConfirm(agent)}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                        {agent.idCallcenterAgent ? (
                          <>
                            <button
                              className="ccv2-btn-icon"
                              style={{ fontSize: '13px', padding: '2px 6px', opacity: forceLoading[agent.agentKey] ? 0.5 : 1 }}
                              disabled={!!forceLoading[agent.agentKey]}
                              onClick={() => handleForceSchedule(agent.agentKey, 'force-enable')}
                              title="Forçar escala ON"
                            >
                              ▶
                            </button>
                            <button
                              className="ccv2-btn-icon"
                              style={{ fontSize: '13px', padding: '2px 6px', opacity: forceLoading[agent.agentKey] ? 0.5 : 1 }}
                              disabled={!!forceLoading[agent.agentKey]}
                              onClick={() => handleForceSchedule(agent.agentKey, 'force-disable')}
                              title="Forçar escala OFF"
                            >
                              ⏸
                            </button>
                            {(scheduleStates[agent.agentKey] === 'forced_on' || scheduleStates[agent.agentKey] === 'forced_off') && (
                              <button
                                className="ccv2-btn-icon"
                                style={{ fontSize: '11px', padding: '2px 6px' }}
                                onClick={() => handleForceSchedule(agent.agentKey, 'clear-force')}
                                title="Limpar force"
                              >
                                ✕
                              </button>
                            )}
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="ccv2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccv2-modal ccv2-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header">
              <h2>{editingAgent ? 'Editar Agente' : 'Novo Agente'}</h2>
              <button className="ccv2-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ccv2-modal-body">
                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Nome do Agente</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome completo"
                      required
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Ramal</label>
                    <input
                      type="text"
                      value={formData.extension}
                      onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                      placeholder="Ex: 1001"
                      required
                    />
                  </div>
                </div>

                <div className="ccv2-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Skills</label>
                  <div className="ccv2-checkbox-grid">
                    {skills.length === 0 ? (
                      <p className="ccv2-hint">Nenhuma skill cadastrada</p>
                    ) : (
                      skills.map((skill) => (
                        <label key={skill.skillKey} className="ccv2-checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.selectedSkills.includes(skill.skillKey)}
                            onChange={() => toggleSkill(skill.skillKey)}
                          />
                          <span>{skill.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="ccv2-form-group">
                  <label>Filas</label>
                  <div className="ccv2-queue-select-list">
                    {queues.length === 0 ? (
                      <p className="ccv2-hint">Nenhuma fila cadastrada</p>
                    ) : (
                      queues.map((queue) => {
                        const selected = formData.selectedQueues.find((q) => q.queueKey === queue.queueKey);
                        return (
                          <div key={queue.queueKey} className="ccv2-queue-select-row">
                            <label className="ccv2-checkbox-item">
                              <input
                                type="checkbox"
                                checked={!!selected}
                                onChange={() => toggleQueue(queue.queueKey)}
                              />
                              <span className="ccv2-queue-select-name" title={queue.name}>
                                {displayQueue(queue.name, effectivePrefix)}
                              </span>
                            </label>
                            {selected && (
                              <span className="ccv2-penalty-group">
                                <span>Penalidade</span>
                                <input
                                  type="number"
                                  className="ccv2-penalty-input"
                                  value={selected.penalty}
                                  onChange={(e) => setQueuePenalty(queue.queueKey, parseInt(e.target.value, 10) || 0)}
                                  min="0"
                                  max="100"
                                  title="Penalty (0-100)"
                                />
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="ccv2-modal-footer">
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ccv2-btn ccv2-btn-primary">
                  {editingAgent ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="ccv2-p5-modal-overlay" onClick={closeDeleteConfirm}>
          <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-p5-modal-header" style={{ background: 'linear-gradient(135deg, #e74c3c, #c0392b)', borderRadius: '14px 14px 0 0' }}>
              <h3 className="ccv2-p5-modal-title" style={{ color: '#fff' }}>Confirmar Exclusão</h3>
              <button className="ccv2-p5-modal-close" style={{ color: '#fff' }} onClick={closeDeleteConfirm}>×</button>
            </div>
            <div className="ccv2-p5-modal-body">
              <p>
                Tem certeza que deseja excluir o agente{' '}
                <strong>{deleteConfirm.displayName || deleteConfirm.name || deleteConfirm.extension}</strong>
                {' '}(ramal {deleteConfirm.extension || extractExtension(deleteConfirm.agentKey)})?
              </p>
              <div style={{ background: '#fff3cd', borderLeft: '4px solid #f39c12', padding: '0.75rem', borderRadius: 4, marginTop: '0.75rem', fontSize: '0.875rem' }}>
                O agente será removido de todas as filas. O ramal (endpoint SIP) não será excluído.
              </div>
              {deleteError && (
                <div style={{ background: '#fdecea', borderLeft: '4px solid #e74c3c', color: '#b91c1c', padding: '0.75rem', borderRadius: 4, marginTop: '0.75rem', fontSize: '0.875rem' }}>
                  ⚠️ {deleteError}
                </div>
              )}
            </div>
            <div className="ccv2-p5-modal-footer">
              <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={closeDeleteConfirm} disabled={deleting}>
                Cancelar
              </button>
              <button className="ccv2-p5-btn ccv2-p5-btn-danger" disabled={deleting} onClick={confirmDelete}>
                {deleting ? '⌛' : '🗑️'} Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentsConfig;
